import { useEffect, useState } from "react";
import { Building2, Users } from "lucide-react";
import { AddressBlock } from "./AddressBlock";
import { detectRechtsform } from "./detectRechtsform";
import { clipDateYear } from "./dateUtils";
import { DateField } from "./DateField";
import { TitelSelect } from "./TitelSelect";

/**
 * Gegenseite (opponent) — mirrors the Mandant input layout (Anrede buttons,
 * conditional Firma block, etc.) but all fields are optional (no asterisks).
 *
 * value shape (kept compatible with backend OpponentInfo):
 *  { bekannt: bool, is_firma: bool, anrede, titel, vorname, name, geburtsdatum,
 *    strasse, hausnummer, plz, ort, country_code,
 *    mobiltelefon, festnetz, fax, email,
 *    rechtsform, register_art, register_nummer, registergericht,
 *    vertreter, vertreter_strasse, vertreter_hausnummer, vertreter_plz,
 *    vertreter_ort, vertreter_country_code }
 *
 * `bekannt` is derived from "any data entered" on submit; we just keep
 * a top-level toggle here so users can skip the section entirely.
 */
const ANREDEN_GEGNER = ["Herr", "Frau", "Firma"];

export const Gegenseite = ({ value, onChange, searchPlaces }) => {
  const set = (k) => (v) => onChange({ ...value, [k]: v });

  const isFirma = value.anrede === "Firma" || value.is_firma === true;
  const isCustomAnrede =
    value.anrede && !ANREDEN_GEGNER.includes(value.anrede);
  const [customMode, setCustomMode] = useState(isCustomAnrede);

  // Auto-detect rechtsform when Firma + name entered
  useEffect(() => {
    if (isFirma && value.name) {
      const rf = detectRechtsform(value.name);
      if (rf && rf !== value.rechtsform) {
        onChange({ ...value, rechtsform: rf });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.name, isFirma]);

  const pickAnrede = (a) => {
    setCustomMode(false);
    onChange({ ...value, anrede: a, is_firma: a === "Firma" });
  };

  const mainAddress = {
    strasse: value.strasse || "",
    hausnummer: value.hausnummer || "",
    plz: value.plz || "",
    ort: value.ort || "",
    country_code: value.country_code || "DE",
  };
  const onMainAddressChange = (next) =>
    onChange({
      ...value,
      strasse: next.strasse,
      hausnummer: next.hausnummer,
      plz: next.plz,
      ort: next.ort,
      country_code: next.country_code,
    });

  const vertreterAddress = {
    strasse: value.vertreter_strasse || "",
    hausnummer: value.vertreter_hausnummer || "",
    plz: value.vertreter_plz || "",
    ort: value.vertreter_ort || "",
    country_code: value.vertreter_country_code || "DE",
  };
  const onVertreterAddressChange = (next) =>
    onChange({
      ...value,
      vertreter_strasse: next.strasse,
      vertreter_hausnummer: next.hausnummer,
      vertreter_plz: next.plz,
      vertreter_ort: next.ort,
      vertreter_country_code: next.country_code,
    });

  return (
    <div className="space-y-6">
      {/* Toggle: bekannt? */}
      <div>
        <span className="eyebrow text-[#505a74]">
          Ist die Gegenseite Ihnen bekannt?
        </span>
        <div className="mt-3 flex gap-2 flex-wrap">
          <button
            type="button"
            data-testid="gegner-bekannt-ja"
            onClick={() => onChange({ ...value, bekannt: true })}
            className={`px-4 py-2.5 rounded-full text-[13px] font-semibold border transition ${
              value.bekannt
                ? "bg-[#0a1128] text-white border-[#0a1128]"
                : "bg-white text-[#0a1128] border-[#e8e6df] hover:border-[#0a1128]"
            }`}
          >
            Ja, bekannt
          </button>
          <button
            type="button"
            data-testid="gegner-bekannt-nein"
            onClick={() => onChange({ ...value, bekannt: false })}
            className={`px-4 py-2.5 rounded-full text-[13px] font-semibold border transition ${
              !value.bekannt
                ? "bg-[#0a1128] text-white border-[#0a1128]"
                : "bg-white text-[#0a1128] border-[#e8e6df] hover:border-[#0a1128]"
            }`}
          >
            Nicht bekannt / nicht zutreffend
          </button>
        </div>
        <p className="mt-2 text-[12px] text-[#505a74]">
          Alle weiteren Angaben sind optional — geben Sie ein, was bekannt ist.
        </p>
      </div>

      {value.bekannt && (
        <>
          {/* Anrede (buttons like Mandant) */}
          <div className="pt-2 border-t border-[#e8e6df]">
            <span className="eyebrow text-[#505a74]">Anrede</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {ANREDEN_GEGNER.map((a) => (
                <button
                  key={a}
                  type="button"
                  data-testid={`gegner-anrede-${a.toLowerCase()}`}
                  onClick={() => pickAnrede(a)}
                  className={`px-4 py-2 rounded-full text-[13px] font-semibold border transition ${
                    value.anrede === a && !customMode
                      ? "bg-[#0a1128] text-white border-[#0a1128]"
                      : "bg-white text-[#0a1128] border-[#e8e6df] hover:border-[#0a1128]"
                  }`}
                >
                  {a}
                </button>
              ))}
              <button
                type="button"
                data-testid="gegner-anrede-custom-toggle"
                onClick={() => {
                  setCustomMode(true);
                  onChange({ ...value, anrede: "", is_firma: false });
                }}
                className={`px-4 py-2 rounded-full text-[13px] font-semibold border transition ${
                  customMode
                    ? "bg-[#0a1128] text-white border-[#0a1128]"
                    : "bg-white text-[#0a1128] border-[#e8e6df] hover:border-[#0a1128]"
                }`}
              >
                Eigene…
              </button>
            </div>
            {customMode && (
              <input
                type="text"
                data-testid="gegner-anrede-custom-input"
                value={value.anrede || ""}
                onChange={(e) =>
                  onChange({ ...value, anrede: e.target.value, is_firma: false })
                }
                placeholder="Eigene Anrede eingeben"
                className="mt-3 block w-full bg-white border border-[#e8e6df] rounded-sm px-4 py-3 text-[15px] focus:outline-none focus:border-[#0a1128] transition"
              />
            )}
          </div>

          {/* Person fields */}
          {!isFirma && (
            <div className="pt-2 border-t border-[#e8e6df]">
              <div className="eyebrow text-[#505a74] mb-4">Persönliche Angaben</div>
              <div className="grid sm:grid-cols-2 gap-5">
                <TitelSelect id="g-titel" value={value.titel} onChange={set("titel")} />
                <div />
                <GField id="g-vorname" label="Vorname" value={value.vorname} onChange={set("vorname")} />
                <GField id="g-name" label="Name" value={value.name} onChange={set("name")} />
                <DateField
                  id="g-geburtsdatum"
                  label="Geburtsdatum"
                  value={value.geburtsdatum}
                  onChange={set("geburtsdatum")}
                  className="sm:col-span-2"
                />
              </div>
            </div>
          )}

          {/* Firma fields */}
          {isFirma && (
            <div className="pt-2 border-t border-[#e8e6df] space-y-5">
              <div className="eyebrow text-[#d90429] flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5" /> Firmenangaben
              </div>
              <GField
                id="g-firmenname"
                label="Firmenname"
                value={value.name}
                onChange={set("name")}
                placeholder="z. B. Mustermann GmbH"
              />
              {value.rechtsform && (
                <div className="flex items-center gap-3 text-[12px] -mt-2">
                  <span className="eyebrow text-[#505a74]">
                    Erkannte Rechtsform:
                  </span>
                  <span className="font-display font-bold text-[#0a1128]">
                    {value.rechtsform}
                  </span>
                </div>
              )}
              <div className="pt-2">
                <div className="eyebrow text-[#505a74] mb-4">
                  Handelsregister (optional)
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <GField id="g-registergericht" label="Registergericht" value={value.registergericht} onChange={set("registergericht")} className="sm:col-span-2" />
                  <GField id="g-register-art" label="Register-Art" value={value.register_art} onChange={set("register_art")} placeholder="z. B. HRB" />
                  <GField id="g-register-nummer" label="Registernummer" value={value.register_nummer} onChange={set("register_nummer")} />
                </div>
              </div>
            </div>
          )}

          {/* Main address (always when bekannt) */}
          <div className="pt-2 border-t border-[#e8e6df]">
            <div className="eyebrow text-[#505a74] mb-4">
              {isFirma ? "Firmenanschrift" : "Adresse"}
            </div>
            <AddressBlock
              value={mainAddress}
              onChange={onMainAddressChange}
              search={searchPlaces}
              idPrefix="form-gegner-address"
              searchPlaceholder={
                isFirma
                  ? "z. B. Musterstraße 1, 50667 Köln"
                  : "z. B. Hauptstraße 79, Lohmar"
              }
            />
          </div>

          {/* Vertreten durch (only for Firma) */}
          {isFirma && (
            <div className="pt-2 border-t border-[#e8e6df]">
              <div className="eyebrow text-[#d90429] mb-4 flex items-center gap-2">
                <Users className="w-3.5 h-3.5" /> Vertreten durch
              </div>
              <GField id="g-vertreter" label="Name des Vertreters" value={value.vertreter} onChange={set("vertreter")} />
              <div className="mt-5">
                <div className="eyebrow text-[#505a74] mb-3">
                  Anschrift des Vertreters
                </div>
                <p className="text-[12px] text-[#505a74] mb-3">
                  (falls abweichend von der Firmenanschrift — sonst freilassen)
                </p>
                <AddressBlock
                  value={vertreterAddress}
                  onChange={onVertreterAddressChange}
                  search={searchPlaces}
                  idPrefix="form-gegner-vertreter-address"
                  searchPlaceholder="Anschrift des Vertreters"
                />
              </div>
            </div>
          )}

          {/* Kontakt */}
          <div className="pt-2 border-t border-[#e8e6df]">
            <div className="eyebrow text-[#505a74] mb-4">Kontaktdaten</div>
            <div className="grid sm:grid-cols-2 gap-5">
              <GField id="g-mobil" type="tel" label="Telefon" value={value.mobiltelefon} onChange={set("mobiltelefon")} />
              <GField id="g-festnetz" type="tel" label="Festnetz" value={value.festnetz} onChange={set("festnetz")} />
              <GField id="g-fax" type="tel" label="Fax" value={value.fax} onChange={set("fax")} />
              <GField id="g-email" type="email" label="E-Mail" value={value.email} onChange={set("email")} />
            </div>
          </div>

          {/* Anwaltlich vertreten? */}
          <div className="pt-2 border-t border-[#e8e6df]">
            <div className="eyebrow text-[#505a74] mb-3">
              Ist die Gegenseite bereits anwaltlich vertreten?
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                data-testid="gegner-lawyer-ja"
                onClick={() =>
                  onChange({ ...value, lawyer_known: true })
                }
                className={`px-4 py-2.5 rounded-full text-[13px] font-semibold border transition ${
                  value.lawyer_known
                    ? "bg-[#0a1128] text-white border-[#0a1128]"
                    : "bg-white text-[#0a1128] border-[#e8e6df] hover:border-[#0a1128]"
                }`}
              >
                Ja
              </button>
              <button
                type="button"
                data-testid="gegner-lawyer-nein"
                onClick={() =>
                  onChange({ ...value, lawyer_known: false, lawyer_name: "" })
                }
                className={`px-4 py-2.5 rounded-full text-[13px] font-semibold border transition ${
                  !value.lawyer_known
                    ? "bg-[#0a1128] text-white border-[#0a1128]"
                    : "bg-white text-[#0a1128] border-[#e8e6df] hover:border-[#0a1128]"
                }`}
              >
                Nein / unbekannt
              </button>
            </div>
            {value.lawyer_known && (
              <div className="mt-5 grid sm:grid-cols-2 gap-5">
                <GField
                  id="g-lawyer-name"
                  label="Kanzlei / Rechtsanwalt(in)"
                  value={value.lawyer_name}
                  onChange={set("lawyer_name")}
                  placeholder="z. B. Kanzlei Müller & Partner"
                  className="sm:col-span-2"
                />
                <GField
                  id="g-lawyer-aktenzeichen"
                  label="Aktenzeichen der Gegenkanzlei"
                  value={value.lawyer_aktenzeichen}
                  onChange={set("lawyer_aktenzeichen")}
                  placeholder="z. B. 123/24"
                  className="sm:col-span-2"
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const GField = ({ id, label, value, onChange, type = "text", placeholder = "", className = "" }) => {
  const isTel = type === "tel";
  return (
  <label className={`block ${className}`}>
    <span className="eyebrow text-[#505a74]">{label}</span>
    <input
      type={type}
      inputMode={isTel ? "tel" : undefined}
      pattern={isTel ? "[0-9+\\s\\-()/]*" : undefined}
      value={value || ""}
      data-testid={`form-input-${id}`}
      onChange={(e) => {
        const v = type === "date" ? clipDateYear(e.target.value) : e.target.value;
        onChange(v);
      }}
      max={type === "date" ? "9999-12-31" : undefined}
      placeholder={placeholder}
      className="mt-2 block w-full bg-white border border-[#e8e6df] rounded-sm px-4 py-3 text-[15px] focus:outline-none focus:border-[#0a1128] transition"
    />
  </label>
  );
};
